import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useHttp from '../../../hooks/http.hook';
import { rewardsFromDiscountMap } from '../../../utils/productGroups';
import CreateBusinessHeader from '../../business/components/CreateBusinessHeader';
import CreateFooter from '../../business/components/CreateFooter';
import PromotionNameImageForm from '../components/PromotionNameImageForm';
import DeletePromotionButton from '../components/DeletePromotionButton';
import PromotionProductsPicker, {
  PromotionPickerOpenButton,
} from '../components/PromotionProductsPicker';
import { createCombinedDiscountPromotion } from '../handlers/combinedDiscountPromotion';
import '../../business/Business.css';
import './CreateCombinedDiscountPromotionPage.css';

function CreateCombinedDiscountPromotionPage() {
  const navigate = useNavigate();
  const { promotionId } = useParams();
  const { requestWithMeta } = useHttp();
  const isEdit = !!promotionId;

  const [name, setName] = React.useState('');
  const [imageUrl, setImageUrl] = React.useState('');
  const [isImageUploading, setIsImageUploading] = React.useState(false);
  const [count, setCount] = React.useState('');
  const [promotionLoading, setPromotionLoading] = React.useState(false);
  const [conditionIds, setConditionIds] = React.useState({});
  const [discounts, setDiscounts] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);
  const [openPicker, setOpenPicker] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    if (!isEdit) return;

    setPromotionLoading(true);
    setSubmitError(null);

    (async () => {
      try {
        const res = await requestWithMeta(`/operator/promotions/${encodeURIComponent(promotionId)}`, 'GET');
        if (cancelled) return;
        if (!res?.ok) {
          setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось загрузить акцию');
          return;
        }
        const promo = res?.data ?? {};
        setName(promo?.name ?? '');
        setImageUrl(promo?.imageUrl ?? promo?.image_url ?? '');

        const conditions = Array.isArray(promo?.conditions) ? promo.conditions : [];
        const cond = conditions[0] || {};
        const meta = cond?.metaData ?? cond?.meta_data ?? {};
        const countRaw = meta?.count;
        if (countRaw !== null && countRaw !== undefined) setCount(String(countRaw));

        const nextCondition = {};
        const productIds = Array.isArray(meta?.productIds) ? meta.productIds : [];
        productIds.forEach((id) => {
          if (id == null) return;
          nextCondition[String(id)] = true;
        });
        setConditionIds(nextCondition);

        const rewards = Array.isArray(promo?.rewards) ? promo.rewards : [];
        const nextDiscounts = {};
        rewards.forEach((r) => {
          const pid = r?.productId ?? r?.product_id;
          const d = r?.discount;
          if (pid == null) return;
          if (d === null || d === undefined) return;
          const n = Number(d);
          if (!Number.isFinite(n)) return;
          nextDiscounts[pid] = n;
        });
        setDiscounts(nextDiscounts);
      } catch (e) {
        if (!cancelled) setSubmitError(e?.message || 'Не удалось загрузить акцию');
      } finally {
        if (!cancelled) setPromotionLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isEdit, promotionId, requestWithMeta]);

  const toggleConditionId = (productId) => {
    setConditionIds((prev) => {
      const key = String(productId);
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = true;
      }
      return next;
    });
  };

  const conditionProductIds = React.useMemo(
    () =>
      Object.entries(conditionIds)
        .filter(([, v]) => !!v)
        .map(([k]) => (Number.isNaN(Number(k)) ? k : Number(k))),
    [conditionIds]
  );

  const handleDiscountChange = (productId, value) => {
    const num = value === '' ? '' : Math.min(100, Math.max(0, Number(value) || 0));
    setDiscounts((prev) => ({ ...prev, [productId]: num }));
  };

  const rewardsWithDiscount = React.useMemo(
    () => rewardsFromDiscountMap(discounts),
    [discounts]
  );

  const numericCount = React.useMemo(() => {
    const normalized = String(count).replace(',', '.').trim();
    if (!normalized) return NaN;
    const n = Number(normalized);
    if (!Number.isFinite(n)) return NaN;
    return Math.max(1, Math.floor(n));
  }, [count]);

  const canSubmit =
    name.trim().length > 0 &&
    Number.isFinite(numericCount) &&
    numericCount > 0 &&
    conditionProductIds.length > 0 &&
    rewardsWithDiscount.length > 0 &&
    !submitting &&
    !isImageUploading &&
    !promotionLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const res = isEdit
        ? await requestWithMeta(`/operator/promotions/${encodeURIComponent(promotionId)}`, 'PUT', {
            name: name.trim(),
            imageUrl: imageUrl.trim() || null,
            conditions: [
              { typeId: 2, metaData: { count: numericCount, productIds: conditionProductIds } }
            ],
            rewards: rewardsWithDiscount
          })
        : await createCombinedDiscountPromotion(requestWithMeta, {
            name: name.trim(),
            imageUrl: imageUrl.trim() || null,
            count: numericCount,
            conditionProductIds,
            rewards: rewardsWithDiscount,
          });
      if (res.ok) {
        navigate('/promotions');
        return;
      }
      setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось создать акцию');
    } catch (e) {
      setSubmitError(e?.message || 'Ошибка при создании акции');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (openPicker === 'condition') {
    return (
      <PromotionProductsPicker
        title="Товары в условии"
        subtitle="Выберите товары для набора"
        mode="select"
        selectedIds={conditionIds}
        onToggle={toggleConditionId}
        onClose={() => setOpenPicker(null)}
      />
    );
  }

  if (openPicker === 'reward') {
    return (
      <PromotionProductsPicker
        title="Скидки по товарам"
        subtitle="Укажите скидку по каждому товару"
        mode="discount"
        discounts={discounts}
        onDiscountChange={handleDiscountChange}
        onClose={() => setOpenPicker(null)}
      />
    );
  }

  return (
    <div className="create-combined-disc-promo-root">
      <CreateBusinessHeader
        title={'Набор + скидки'}
        subtitle="Скидки по товарам при покупке набора"
        onBack={handleBack}
      />

      <main className="create-combined-disc-promo-content">
        <PromotionNameImageForm
          name={name}
          onNameChange={setName}
          imageUrl={imageUrl}
          onImageUrlChange={setImageUrl}
          onImageUploadLoadingChange={setIsImageUploading}
        />

        <section className="shop-create-section">
          <label className="shop-create-label" htmlFor="combined-disc-count">
            Количество товаров в наборе <b style={{ color: 'red' }}>*</b>
          </label>
          <input
            id="combined-disc-count"
            className="shop-create-input"
            placeholder="Например, 3"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            autoComplete="off"
            inputMode="numeric"
          />
        </section>

        <section className="shop-create-section">
          <PromotionPickerOpenButton
            title="Товары в условии"
            hint={
              conditionProductIds.length > 0
                ? `Выбрано: ${conditionProductIds.length}`
                : 'Выбрать товары'
            }
            onClick={() => setOpenPicker('condition')}
          />
          <PromotionPickerOpenButton
            title="Скидки по товарам"
            hint={
              rewardsWithDiscount.length > 0
                ? `Выбрано: ${rewardsWithDiscount.length}`
                : 'Указать скидки'
            }
            onClick={() => setOpenPicker('reward')}
          />
        </section>

        {submitError ? (
          <div className="create-combined-disc-promo-error">{submitError}</div>
        ) : null}
      </main>

      <CreateFooter
        disabled={!canSubmit}
        onCreate={handleSubmit}
        label={
          submitting
            ? isEdit
              ? 'Сохранение…'
              : 'Создание…'
            : isEdit
              ? 'Сохранить'
              : 'Создать акцию'
        }
        leading={
          isEdit ? (
            <DeletePromotionButton
              promotionId={promotionId}
              promotionName={name}
              requestWithMeta={requestWithMeta}
              disabled={submitting}
              onDeleted={() => navigate('/promotions')}
            />
          ) : null
        }
      />
    </div>
  );
}

export default CreateCombinedDiscountPromotionPage;

